import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, MessageCircle, Star, PenTool, Search, Smartphone, MousePointerClick, ShoppingBag, Bot, Workflow, Target } from "lucide-react";
import SEO from "../components/SEO";
import { faqSchema } from "../data/schema";
import { Reveal, Stagger, Item, Chapter, MaskedLine } from "../components/Reveal";
import { openLeadPopup } from "../components/Navbar";
import { TESTIMONIALS } from "../data/testimonials";
import { waLink, CONTEXT_MESSAGES, WA_NUMBER_DISPLAY } from "../lib/whatsapp";
import { track } from "../lib/analytics";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";

const Stars = ({ count = 5 }) => (
  <div className="flex gap-0.5 text-ember" aria-label={`${count} star rating`}>
    {Array.from({ length: count }).map((_, i) => (
      <Star key={i} className="h-3.5 w-3.5" fill="currentColor" />
    ))}
  </div>
);

const ReviewCard = ({ t, index }) => (
  <article
    data-testid={`reviews-card-${index}`}
    className="flex h-full flex-col rounded-2xl border border-white/10 bg-ink p-6 transition-all duration-300 hover:-translate-y-1 hover:border-ember/30 sm:p-7"
  >
    <div className="flex items-center justify-between">
      <Stars count={t.rating} />
      <span className="rounded-full border border-white/10 px-2.5 py-0.5 font-mono text-[10px] uppercase tracking-[0.15em] text-white/40">
        {t.country}
      </span>
    </div>
    <h3 className="mt-4 font-display text-lg font-bold tracking-tight">"{t.title}"</h3>
    <p className="mt-3 flex-1 text-sm leading-relaxed text-white/55">{t.quote}</p>
    <div className="mt-6 flex items-center gap-3 border-t border-white/10 pt-4">
      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-ember/15 font-display text-xs font-bold tracking-wide text-ember">
        {t.initials}
      </span>
      <div className="min-w-0">
        <p className="truncate font-display text-sm font-bold">{t.name}</p>
        <p className="truncate text-xs text-white/45">{t.designation} · {t.location}</p>
      </div>
    </div>
  </article>
);

const VALUE_CARDS = [
  { icon: PenTool, h: "Modern Website Design", p: "Premium, clean design that makes businesses look established and trustworthy from the first visit." },
  { icon: Search, h: "SEO-Focused Development", p: "Technical foundations, content structure and local SEO built into every website from day one." },
  { icon: Smartphone, h: "Mobile-Friendly Websites", p: "Fast, thumb-friendly experiences on the phones customers actually use." },
  { icon: MousePointerClick, h: "Conversion-Focused UX", p: "Pages structured to turn visitors into enquiries — not just to look good." },
  { icon: ShoppingBag, h: "Shopify Development", p: "Custom Shopify stores and Shopify SEO that turn traffic into orders." },
  { icon: Bot, h: "AI Chatbot Integration", p: "Chatbots trained on your business, answering customers and capturing leads 24/7." },
  { icon: Workflow, h: "AI Business Automation", p: "Lead follow-up, WhatsApp replies and repetitive workflows that run themselves." },
  { icon: Target, h: "Lead Generation", p: "Websites engineered backwards from the enquiry — the metric that actually matters." },
];

const REVIEW_SERVICES = [
  { to: "/website-development/", label: "Website Development" },
  { to: "/seo-services/", label: "SEO Services" },
  { to: "/shopify-website-development/", label: "Shopify Development" },
  { to: "/shopify-seo/", label: "Shopify SEO" },
  { to: "/ai-chatbot-development/", label: "AI Chatbot Development" },
  { to: "/ai-business-automation/", label: "AI Business Automation" },
  { to: "/lead-generation-websites/", label: "Lead Generation Websites" },
  { to: "/website-redesign/", label: "Website Redesign" },
];

const REVIEW_FAQS = [
  { q: "What services does Grotech Web provide?", a: "Website development, web design, SEO services, Shopify website development, Shopify SEO, AI chatbot development, AI business automation, lead generation websites, website redesign and ecommerce development — for businesses in India and the USA." },
  { q: "Does Grotech Web provide SEO services?", a: "Yes — technical SEO, keyword-mapped content, local SEO and monthly growth plans. Timelines are honest (typically 3–6 months for meaningful movement) and we never guarantee specific rankings." },
  { q: "Does Grotech Web build Shopify websites?", a: "Yes — custom Shopify store development, store redesign, product page optimization and Shopify SEO, for stores in India, the USA and the UAE." },
  { q: "Can Grotech Web add an AI chatbot to my website?", a: "Yes — custom AI chatbots trained on your business content, embedded on any website (including WordPress and Shopify), with lead qualification and WhatsApp handoff." },
  { q: "Can Grotech Web automate business processes using AI?", a: "Yes — lead response and follow-up, WhatsApp automation, CRM workflows, review requests and repetitive customer support. We start where revenue leaks fastest, usually lead response." },
  { q: "Does Grotech Web work with businesses in the USA?", a: "Yes — remotely, with async previews on your schedule and calls in US-friendly hours. The same premium quality at a fraction of US agency rates." },
  { q: "Does Grotech Web work with businesses in India?", a: "Yes — businesses across India including Mumbai, Delhi, Noida and Gurgaon, with WhatsApp-first communication and affordable, transparent pricing." },
];

export default function Reviews() {
  const [tab, setTab] = useState("india");
  const india = TESTIMONIALS.filter((t) => t.country === "India");
  const usa = TESTIMONIALS.filter((t) => t.country === "USA");

  useEffect(() => {
    track("review_page_view", {});
  }, []);

  return (
    <main className="pt-36 lg:pt-44">
      <SEO
        title="Grotech Web Reviews | Website, SEO, Shopify & AI Services"
        description="Grotech Web reviews and client testimonials — feedback on our website development, SEO services, Shopify development, AI chatbot and AI automation work for businesses in India and the USA."
        path="/reviews"
        schema={faqSchema(REVIEW_FAQS)}
        breadcrumbs={[{ name: "Home", path: "/" }, { name: "Reviews", path: "/reviews" }]}
      />

      <section className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16" data-testid="reviews-hero">
        <nav className="font-mono text-[11px] uppercase tracking-[0.25em] text-white/40" aria-label="Breadcrumb">
          <Link to="/" className="hover:text-white">Home</Link>
          <span className="mx-2 text-ember">/</span>
          <span className="text-white/70">Reviews</span>
        </nav>
        <h1 className="mt-8 max-w-4xl font-display text-4xl font-black leading-[1.04] tracking-tight sm:text-5xl lg:text-6xl">
          <MaskedLine>Grotech Web Reviews</MaskedLine>
          <MaskedLine delay={0.15}>
            <span className="text-white/35">& Client Testimonials.</span>
          </MaskedLine>
        </h1>
        <Reveal hero delay={0.3}>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-white/60 sm:text-lg">
            Explore feedback about our website development, SEO, Shopify, AI chatbot and AI
            automation services — from businesses in India and the USA.
          </p>
          <p className="mt-3 max-w-2xl text-xs text-white/35">
            Testimonials reflect client feedback provided to Grotech Web. Individual experiences may vary.
          </p>
        </Reveal>

        <Reveal hero delay={0.35}>
          <div className="mt-10 inline-flex rounded-full border border-white/10 bg-ink p-1" data-testid="reviews-toggle">
            {[
              { key: "india", label: `India · ${india.length}` },
              { key: "usa", label: `USA · ${usa.length}` },
            ].map((t) => (
              <button
                key={t.key}
                data-testid={`reviews-tab-${t.key}`}
                onClick={() => { setTab(t.key); track("cta_click", { location: "reviews", tab: t.key }); }}
                className={`rounded-full px-6 py-2.5 font-display text-xs font-bold uppercase tracking-wider transition-colors ${
                  tab === t.key ? "bg-ember text-white" : "text-white/50 hover:text-white"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </Reveal>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-14 sm:px-8 lg:px-16" data-testid="reviews-grid">
        <Stagger key={tab} className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {(tab === "india" ? india : usa).map((t, i) => (
            <Item key={t.name} className="h-full">
              <ReviewCard t={t} index={i} />
            </Item>
          ))}
        </Stagger>
      </section>

      <section className="border-t border-line bg-ink py-20 lg:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16">
          <Chapter index="01" label="What Clients Value" />
          <Reveal delay={0.1}>
            <h2 className="mt-6 max-w-2xl font-display text-3xl font-bold leading-[1.05] tracking-tight sm:text-4xl">
              What Clients Value About Grotech Web
            </h2>
          </Reveal>
          <Stagger className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {VALUE_CARDS.map((c) => (
              <Item key={c.h} className="rounded-2xl border border-white/10 bg-obsidian p-6 transition-colors hover:border-ember/30">
                <c.icon className="h-5 w-5 text-ember" />
                <h3 className="mt-4 font-display text-base font-bold tracking-tight">{c.h}</h3>
                <p className="mt-2 text-sm leading-relaxed text-white/50">{c.p}</p>
              </Item>
            ))}
          </Stagger>

          <Reveal delay={0.1}>
            <div className="mt-16">
              <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-white/40">Our Services</p>
              <div className="mt-5 flex flex-wrap gap-3">
                {REVIEW_SERVICES.map((s) => (
                  <Link
                    key={s.to}
                    to={s.to}
                    data-testid={`reviews-service-${s.to.slice(1)}`}
                    className="group flex items-center gap-1.5 rounded-full border border-white/15 px-5 py-2.5 text-sm text-white/70 transition-colors hover:border-ember/50 hover:text-white"
                  >
                    {s.label}
                    <ArrowUpRight className="h-3.5 w-3.5 text-white/25 transition-colors group-hover:text-ember" />
                  </Link>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-4xl px-4 py-20 sm:px-8 lg:py-24">
        <Chapter index="02" label="Questions" />
        <Reveal delay={0.1}>
          <h2 className="mt-6 font-display text-3xl font-bold tracking-tight sm:text-4xl">
            Before You Ask
          </h2>
        </Reveal>
        <div className="mt-8">
          <Accordion type="single" collapsible data-testid="reviews-faq">
            {REVIEW_FAQS.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="border-white/10">
                <AccordionTrigger
                  data-testid={`reviews-faq-trigger-${i}`}
                  onClick={() => track("faq_expand", { question: f.q, location: "reviews" })}
                  className="text-left font-display text-base font-semibold text-white hover:text-ember hover:no-underline"
                >
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-sm leading-relaxed text-white/55">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      <section className="border-t border-line bg-ink py-16 lg:py-20">
        <div className="mx-auto max-w-4xl px-4 text-center sm:px-8">
          <Reveal>
            <h2 className="font-display text-3xl font-bold leading-[1.05] tracking-tight sm:text-4xl">
              Your competitors are improving their websites. <span className="text-ember">Is yours ready?</span>
            </h2>
            <p className="mx-auto mt-4 max-w-lg text-sm leading-relaxed text-white/55">
              Get a free website growth review — find opportunities to improve your website's SEO,
              conversion rate, lead generation and customer experience.
            </p>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
              <button
                data-testid="reviews-fomo-cta"
                onClick={() => { track("cta_click", { location: "reviews_fomo" }); openLeadPopup(); }}
                className="group flex items-center gap-2 rounded-full bg-ember px-7 py-4 font-display text-sm font-bold uppercase tracking-wider text-white transition-colors hover:bg-ember-hover"
              >
                Get My Free Growth Review
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </button>
              <a
                href={waLink(CONTEXT_MESSAGES.general)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="reviews-fomo-whatsapp"
                onClick={() => track("whatsapp_click", { location: "reviews_fomo" })}
                className="flex items-center gap-2 rounded-full border border-[#25D366]/50 px-7 py-4 font-display text-sm font-bold uppercase tracking-wider text-[#25D366] transition-colors hover:bg-[#25D366]/10"
              >
                <MessageCircle className="h-4 w-4" /> WhatsApp Grotech Web
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-16">
        <div className="mx-auto max-w-3xl px-4 text-center sm:px-8">
          <Reveal>
            <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-ember">Share Your Experience</p>
            <h2 className="mt-4 font-display text-2xl font-bold tracking-tight sm:text-3xl">
              Worked with Grotech Web?
            </h2>
            <p className="mx-auto mt-3 max-w-md text-sm text-white/50">
              Your genuine feedback helps other businesses choose with confidence — and takes less
              than a minute on WhatsApp.
            </p>
            <a
              href={waLink(CONTEXT_MESSAGES.leave_review)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="leave-review-btn"
              onClick={() => track("cta_click", { location: "reviews", cta: "leave_review" })}
              className="group mt-7 inline-flex items-center gap-2 rounded-full bg-ember px-7 py-4 font-display text-sm font-bold uppercase tracking-wider text-white transition-colors hover:bg-ember-hover"
            >
              Leave a Review
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </a>
            <p className="mt-5 font-mono text-xs tracking-[0.2em] text-white/35">{WA_NUMBER_DISPLAY}</p>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
